<template>
  <modal
    :id="modalId"
    :componentKey="componentKey"
    ref="modal"
    @close="close"
    is-simplified-modal
    title="Add Knowledge base category"
  >
    <template v-slot:body>
      <div>
        <!-- Title Container with colored background -->
        <div class="bg-primary-extra-lightest p-5">
          <div class="text-sm italic text-gray-700 mb-4">
            All fields with asterisk (*) are mandatory
          </div>

          <form-group>
            <DsInput
              v-model="categoryName"
              type="text"
              placeholder="Category Name *"
              class="w-full"
              required
              ref="nameInput"
              is-simplified
            />
          </form-group>
        </div>

        <!-- Content Container -->
        <div v-if="errors && errors.length > 0" class="px-5 py-5 pb-4">
          <div class="text-sm mb-2">
            <div v-for="message in errors" :key="message" class="text-red-600">
              <span>{{ message }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="w-full">
        <ds-button
          variant="rounded"
          @click="addCategory"
          :label="isLoading ? 'Adding Category...' : 'Add Category'"
          size="extra-small"
          :disabled="!canSubmit"
          class="w-full"
        />
      </div>
    </template>
  </modal>
</template>

<script>
  import Modal from '@/components/Modals/Modal.vue'
  import FormGroup from '@/components/Form/FormGroup.vue'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import MODAL_IDS from '@/constants/modal-ids.js'
  import DsInput from '@/components/Form/DsInput.vue'
  import { createCategory } from '@/api/knowledge-base'

  export default {
    name: 'AddKnowledgeBaseCategoryModal',
    components: {
      DsInput,
      Modal,
      FormGroup
    },
    data () {
      return {
        modalId: 'add-category-modal',
        componentKey: 0,
        categoryName: '',
        errors: [],
        minNameLength: 2,
        isLoading: false
      }
    },
    computed: {
      canSubmit () {
        return this.categoryName && this.categoryName.trim().length >= this.minNameLength && !this.isLoading
      }
    },
    mounted () {
      // Focus the name input when modal opens
      this.$nextTick(() => {
        this.$refs.nameInput?.focus()
      })
    },
    methods: {
      close () {
        // Reset form
        this.categoryName = ''
        this.errors = []
        this.isLoading = false

        // Close modal
        this.$store.commit(UI_MUTATION_TYPES.HIDE_MODAL, MODAL_IDS.ADD_KNOWLEDGE_BASE_CATEGORY)
        this.$emit('close')
      },
      validateForm () {
        this.errors = []

        if (!this.categoryName || this.categoryName.trim().length < this.minNameLength) {
          this.errors.push(`Category name must be at least ${this.minNameLength} characters long`)
        }
      },
      async addCategory () {
        this.validateForm()
        this.componentKey++

        if (this.errors.length > 0) {
          return
        }

        this.isLoading = true

        try {
          const categoryData = {
            name: this.categoryName.trim()
          }

          const response = await createCategory(categoryData)
          const newCategory = response.data

          // Emit event to parent component with the created category
          this.$bus.emit('category-added', newCategory)

          this.close()
        } catch (error) {
          console.error('Failed to create category:', error)

          // Handle validation errors from the server
          if (error.response && error.response.data && error.response.data.errors) {
            // Laravel validation errors are in format: { field: ["error message"] }
            const validationErrors = error.response.data.errors
            this.errors = []

            // Extract all error messages from validation response
            Object.keys(validationErrors).forEach(field => {
              if (Array.isArray(validationErrors[field])) {
                this.errors.push(...validationErrors[field])
              }
            })
          } else if (error.response && error.response.data && error.response.data.message) {
            // Single error message
            this.errors = [error.response.data.message]
          } else {
            // Generic error fallback
            this.errors = error.name
          }

        } finally {
          this.isLoading = false
        }
      }
    }
  }
</script>

